/**
 * Calibration Planning — Operations command centre for sensor calibration.
 * Three tabs (#991): Overview (cockpit), Expired (drill-down), Upcoming (next 3
 * months + 13-week lab queue).
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CalendarClock, RefreshCw } from 'lucide-react';
import { PageShell, UnderlineTabs, type UnderlineTabsTab } from '@/shared/components';
import { usePersistedTab } from '@/shared/hooks/usePersistedFilters';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useCalibrationPlanning } from '../hooks';
import { OverviewTab } from '../components/tabs/OverviewTab';
import { ExpiredTab } from '../components/tabs/ExpiredTab';
import { UpcomingTab } from '../components/tabs/UpcomingTab';
import type { CalibrationPlanningParams } from '../types';

type Tab = 'overview' | 'expired' | 'upcoming';
const VALID_TABS: readonly Tab[] = ['overview', 'expired', 'upcoming'];

export function CalibrationPlanningPage() {
  const { t } = useTranslation('operations');
  const today = new Date().toISOString().slice(0, 10);

  const [tab, setTab] = usePersistedTab<Tab>('calibration-planning', VALID_TABS, 'overview');
  const [asOf, setAsOf] = useState<string>(today);
  const [horizonMonths, setHorizonMonths] = useState<number>(24);
  const [requireContract, setRequireContract] = useState<boolean>(true);

  const params: CalibrationPlanningParams = {
    asOf,
    horizonMonths,
    requireContract,
    upcomingMonths: 3,
  };

  const { data, isPending, isError, refetch, isFetching } = useCalibrationPlanning(params);

  const tabs: readonly UnderlineTabsTab<Tab>[] = [
    { value: 'overview', label: t('calibrationPlanning.tabs.overview', 'Overview') },
    { value: 'expired', label: t('calibrationPlanning.tabs.expired', 'Expired'), badge: data?.expired?.total || undefined },
    { value: 'upcoming', label: t('calibrationPlanning.tabs.upcoming', 'Upcoming'), badge: data?.upcoming?.total || undefined },
  ];

  const controls = (
    <div className="flex flex-wrap items-end gap-3">
      <div className="flex flex-col gap-1">
        <Label htmlFor="cal-as-of" className="text-xs">As of</Label>
        <Input
          id="cal-as-of"
          type="date"
          value={asOf}
          onChange={(e) => setAsOf(e.target.value)}
          className="h-8 w-36 text-sm"
        />
      </div>
      <div className="flex flex-col gap-1">
        <Label htmlFor="cal-horizon" className="text-xs">Horizon</Label>
        <Select value={String(horizonMonths)} onValueChange={(v) => setHorizonMonths(Number(v))}>
          <SelectTrigger id="cal-horizon" className="h-8 w-24 text-sm">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="6">6 mo</SelectItem>
            <SelectItem value="12">12 mo</SelectItem>
            <SelectItem value="24">24 mo</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="flex flex-col gap-1">
        <Label htmlFor="cal-contract" className="text-xs">Covered only</Label>
        <div className="flex items-center gap-2 h-8">
          <Switch id="cal-contract" checked={requireContract} onCheckedChange={setRequireContract} />
          <span className="text-xs text-muted-foreground">{requireContract ? 'Yes' : 'All'}</span>
        </div>
      </div>
      <button
        type="button"
        onClick={() => void refetch()}
        disabled={isFetching}
        className="flex h-8 items-center gap-1 rounded-md border border-input bg-transparent px-3 text-sm shadow-sm hover:bg-accent focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:opacity-50"
        aria-label="Refresh planning data"
      >
        <RefreshCw className={`h-4 w-4 ${isFetching ? 'animate-spin' : ''}`} />
        Refresh
      </button>
    </div>
  );

  return (
    <PageShell
      Icon={CalendarClock}
      subtitle={t('calibrationPlanning.subtitle', 'Calibration scheduling — expired, upcoming, and lab capacity')}
      actions={controls}
      tabs={<UnderlineTabs<Tab> tabs={tabs} active={tab} onChange={setTab} />}
    >
      {isError && (
        <div role="alert" className="flex items-center justify-between rounded-md border border-red-200 bg-red-50 p-4">
          <span className="text-sm text-red-700">
            {t('calibrationPlanning.loadError', 'Failed to load planning data. Check your connection and try again.')}
          </span>
          <button
            type="button"
            onClick={() => void refetch()}
            className="ml-4 rounded-md border border-red-300 px-3 py-1 text-sm text-red-700 hover:bg-red-100"
          >
            {t('calibrationPlanning.retry', 'Retry')}
          </button>
        </div>
      )}

      {tab === 'overview' && (
        <OverviewTab data={data} isPending={isPending} onOpenExpired={() => setTab('expired')} />
      )}
      {tab === 'expired' && data && <ExpiredTab expired={data.expired} />}
      {tab === 'upcoming' && data && <UpcomingTab upcoming={data.upcoming} />}
    </PageShell>
  );
}
